import { getCustomIDStatus, getPrivacy } from "../Components/FilterBars/auditFilters";
import { customThumbnailAuditLabel } from "./filterOptions";
import { parseClaimsArray, resolveScorecardAssetId } from "../Components/Utils/claimsUtils";
import { ClaimDetail, RowData } from "../Interfaces/interfaces";

export const metadataExportRows = (row: RowData) => ({
  "Video ID": row.videoId,
  Privacy: getPrivacy(row),
  CMS: row.CMS,
  "Content Format": row.CreatorContentType,
  "Channel Name": row.Channel_Name,
  "Channel ID": row.YoutubeChannelId,
  "Upload Date": row.timeUploaded,
  "Published Date": row.timePublished,
  "Scheduled Date": row.PublishDate,
  "Channel Manager": row.ChannelManagerName,
  "Channel Manager Email": row.ChannelManagerEmail,
  "Title": row.videoTitle,
  Views: row.views,
  "Custom ID": row.customid,
  "Custom ID Status": getCustomIDStatus(row),
  Description: row.Description,
  "Category": row.category,
  "MFK Status": row.mfk,
  "Custom Thumbnail": customThumbnailAuditLabel(row),
  // "Language Code": row.LanguageCode,
  "Age Restriction": row.ageRestriction,
  "Paid Promo": row.promotion,
  "YouTube License": row.license,
});

const formatClaimValue = (value: unknown): string => {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => formatClaimValue(item))
      .filter((item) => item !== "")
      .join(", ");
  }
  if (typeof value === "object") {
    // Avoid dumping claim objects / structs into Excel cells.
    return "";
  }
  return String(value).trim();
};

/** Join per-claim field values, skipping blanks so we don't get ", , , ,". */
const joinClaimFields = (
  claims: ClaimDetail[],
  pick: (claim: ClaimDetail) => unknown
): string =>
  claims
    .map((claim) => formatClaimValue(pick(claim)))
    .filter((value) => value !== "")
    .join(", ");

const isLikelyEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * Conflict emails only — drop owner names / timestamps accidentally stored in
 * this field, and skip empty claim slots.
 */
const formatConflictEmails = (value: unknown): string => {
  const collected: string[] = [];

  const visit = (entry: unknown) => {
    if (entry == null || entry === "") return;
    if (Array.isArray(entry)) {
      entry.forEach(visit);
      return;
    }
    if (typeof entry === "object") return;
    const text = String(entry).trim();
    if (!text) return;
    // Split comma-separated email lists from Content Owner API.
    for (const part of text.split(/[,;]+/)) {
      const candidate = part.trim();
      if (isLikelyEmail(candidate)) collected.push(candidate);
    }
  };

  visit(value);
  return Array.from(new Set(collected)).join(", ");
};

const asAssetId = (value: unknown): string => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    const first = value.find((v) => v != null && String(v).trim() !== "");
    return first == null ? "" : String(first).trim();
  }
  return String(value).trim();
};

/**
 * If a title was stored as "A123… Song Name", strip the leading asset id.
 * Require a clear delimiter so normal titles are never wiped.
 */
const cleanAssetTitle = (title: unknown, assetId: unknown): string => {
  const raw = formatClaimValue(title);
  if (!raw) return "";
  const id = asAssetId(assetId);
  if (!id || raw === id) return raw === id ? "" : raw;
  if (
    raw.startsWith(`${id} `) ||
    raw.startsWith(`${id}-`) ||
    raw.startsWith(`${id}:`) ||
    raw.startsWith(`${id}_`)
  ) {
    return raw.slice(id.length).replace(/^[\s\-–—:_]+/, "").trim() || raw;
  }
  return raw;
};

const claimsForExport = (
  row: RowData,
  cached: Record<string, ClaimDetail[]>,
  rowField: "thirdPartyClaims" | "syncThirdPartyClaims"
): ClaimDetail[] => {
  const fromCache = cached[row.videoId];
  if (Array.isArray(fromCache) && fromCache.length > 0) return fromCache;
  return parseClaimsArray(row[rowField]) as ClaimDetail[];
};

export const claimsAdsExportRows = (
  row: RowData,
  claimsDetailsForExport: Record<string, ClaimDetail[]>,
  syncClaimsDetailsForExport: Record<string, ClaimDetail[]>,
  assetTitlesByAssetId: Record<string, string> = {},
  assetTitlesByVideoId: Record<string, string> = {}
) => {
  const claims = claimsForExport(row, claimsDetailsForExport, "thirdPartyClaims").map(
    (claim) => enrichClaimWithTitle(claim, assetTitlesByAssetId)
  );
  const syncClaims = claimsForExport(
    row,
    syncClaimsDetailsForExport,
    "syncThirdPartyClaims"
  ).map((claim) => enrichClaimWithTitle(claim, assetTitlesByAssetId));

  const resolvedAssetId = resolveScorecardAssetId(row);
  const primaryAssetId =
    resolvedAssetId
      .split(",")
      .map((id) => id.trim())
      .find((id) => id && id !== "Missing") || "";

  // Prefer live lookups over stored row.assetTitle (often "Missing" until copy runs).
  // Video→AssetTitle is the same path used by claims-ads copy and is most reliable.
  const fromVideo =
    row.videoId && assetTitlesByVideoId[row.videoId]
      ? cleanAssetTitle(assetTitlesByVideoId[row.videoId], primaryAssetId)
      : "";
  const fromAsset =
    primaryAssetId && assetTitlesByAssetId[primaryAssetId]
      ? cleanAssetTitle(assetTitlesByAssetId[primaryAssetId], primaryAssetId)
      : "";
  const fromRow =
    row.assetTitle && row.assetTitle !== "Missing"
      ? cleanAssetTitle(row.assetTitle, primaryAssetId)
      : "";
  const resolvedAssetTitle = fromVideo || fromAsset || fromRow;

  return {
    "Video ID": row.videoId,
    Privacy: row.privacy,
    CMS: row.CMS,
    "Content Format": row.CreatorContentType,
    "Channel Name": row.Channel_Name,
    "Channel ID": row.YoutubeChannelId,
    "Upload Date": row.timeUploaded,
    "Published Date": row.timePublished,
    "Scheduled Date": row.PublishDate,
    "Channel Manager": row.ChannelManagerName,
    "Channel Manager Email": row.ChannelManagerEmail,
    "Video Title": row.videoTitle,
    Views: row.views,
    "Custom ID": row.customid,
    "Asset ID": resolvedAssetId,
    "Asset Title": resolvedAssetTitle,
    "Asset Format": row.assetFormat,
    "Asset Owned": row.ownership,
    "Moonbug Claim": row.claimStatus,
    "Third Party Claim Assets": joinClaimFields(claims, (claim) =>
      asAssetId(claim.assetId)
    ),
    "Third Party Claim Asset Title/s": joinClaimFields(
      claims,
      (claim) => claim.assetTitle
    ),
    "Third Party Claim Time Created": joinClaimFields(
      claims,
      (claim) => claim.timeCreated
    ),
    "Third Party Claim Last Modified": joinClaimFields(
      claims,
      (claim) => claim.timeStatusLastModified
    ),
    "Third Party Claim Content Type/s": joinClaimFields(
      claims,
      (claim) => claim.contentType
    ),
    "Third Party Claim Status": joinClaimFields(
      claims,
      (claim) => claim.claimStatus
    ),
    "Third Party Claim Owner/s": joinClaimFields(
      claims,
      (claim) => claim.ownerName
    ),
    "Third Party Claim Conflict Email/s": joinClaimFields(claims, (claim) =>
      formatConflictEmails(claim.conflictNotificationEmail)
    ),
    "Sync Composition Claims Assets": joinClaimFields(syncClaims, (claim) =>
      asAssetId(claim.assetId)
    ),
    "Sync Composition Claims Asset Title/s": joinClaimFields(
      syncClaims,
      (claim) => claim.assetTitle
    ),
    "Sync Composition Claims Time Created": joinClaimFields(
      syncClaims,
      (claim) => claim.timeCreated
    ),
    "Sync Composition Claims Last Modified": joinClaimFields(
      syncClaims,
      (claim) => claim.timeStatusLastModified
    ),
    "Sync Composition Claims Content Type/s": joinClaimFields(
      syncClaims,
      (claim) => claim.contentType
    ),
    "Sync Composition Claims Status": joinClaimFields(
      syncClaims,
      (claim) => claim.claimStatus
    ),
    "Sync Composition Claims Owner/s": joinClaimFields(
      syncClaims,
      (claim) => claim.ownerName
    ),
    "Sync Composition Claims Conflict Email/s": joinClaimFields(
      syncClaims,
      (claim) => formatConflictEmails(claim.conflictNotificationEmail)
    ),
    "Skippable Ads": row.skippableAdsEnabled,
    "Non-Skippable Ads": row.nonskippableVideoAdsEnabled,
    "Pre-Rolls": row.preroll_count,
    "Mid-Rolls": row.midroll_count,
    "Post-Rolls": row.postroll_count,
    "Midroll Frequency": row.full_midroll_timestamp,
    "Largest Midroll Interval": row.biggest_midroll_interval,
    "Auto Generated Ads": row.auto_generated_breaks
  };
};

const enrichClaimWithTitle = (
  claim: ClaimDetail,
  titlesByAssetId: Record<string, string>
): ClaimDetail => {
  const assetId = asAssetId(claim.assetId);
  const fromMap =
    assetId && titlesByAssetId[assetId]
      ? cleanAssetTitle(titlesByAssetId[assetId], assetId)
      : "";
  const existing = cleanAssetTitle(claim.assetTitle, assetId);
  const assetTitle = fromMap || existing;
  if (!assetTitle) return claim;
  return { ...claim, assetTitle };
};
